/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';

import {
    Stack,
    DividerNew as Divider,
    Alert,
    Text,
    Button,
    Box,
    STATUS,
    Icon,
    useWindowSize,
    InlineBulletList,
    Heading,
    Badge,
    toTitleCase,
    SecurityHelper,
    useFlags,
} from 'sarsaparilla';

import CardMapLayout from 'shared-ui/components/CardMapLayout/CardMapLayout';
import MapViewWithCards from 'shared-ui/components/MapViewWithCards/MapViewWithCards';
import MapCTAWrapper from 'shared-ui/maps/components/MapCTAWrapper';
import MobileSearchResultCard from 'shared-ui/components/SearchResultCard/MobileSearchResultCard';
import { FacilityCalendarContainer } from '../../containers/FacilityPage/FacilityCalendarContainer';
import TourTimeFilter from './FilterBar/TourTimeFilter';

import { tourImageMapType, tourRatingsMapType, toursType } from '../../types';

import { MapContainer } from './MapContainer';
import { TourListPlaceholder } from './TourListPlaceholder';
import { PaginatedTourList } from './PaginatedTourList';
import AvailabilityAlerts from '../common/AvailabilityAlerts';

function makeMobileTourListCards({ tours, tourImageMap, tourTimesById }) {
    const cards = [];

    tours.forEach((item) => {
        const { tour_id: tourId, facility_id: facilityId, tour_name: tourName } = item;
        const tourKey = `${facilityId}_${tourId}`;
        const imageUrl =
            tourImageMap?.[tourKey]?.previewImage?.url ||
            tourImageMap?.[tourKey]?.heroImage?.url ||
            tourImageMap?.[tourKey]?.galleryImages?.[0]?.url;

        const tourTimes = [];

        if (tourTimesById?.[tourId]) {
            tourTimesById[tourId].forEach((timeItem, index) => {
                if (timeItem.isAvailable && index < 4) {
                    tourTimes.push(
                        <li key={timeItem.displayTime}>{timeItem.displayTime}</li>
                    );
                }
            });
        }

        if (tourTimesById?.[tourId]?.length > 4) {
            tourTimes.push(
                <li key="more-items">
                    <Badge>{`+ ${tourTimesById?.[tourId]?.length - 4} more`}</Badge>
                </li>
            );
        }

        const contents = tourTimes.length ? (
            <Stack space="xs">
                <Heading headingLevel={3} appearance="small">
                    Available Times
                </Heading>
                <InlineBulletList>{tourTimes}</InlineBulletList>
            </Stack>
        ) : (
            <Text size="sm" color="gray">
                Enter date to see available times
            </Text>
        );

        cards.push(
            <MobileSearchResultCard
                key={tourId}
                linkUrl={`/timed-entry/${facilityId}/ticket/${tourId}`}
                imageUrl={imageUrl}
                heading={tourName}
            >
                {contents}
            </MobileSearchResultCard>
        );
    });

    return cards;
}

export function ToursTab({
    toursLoadingStatus,
    isMapLoading,
    isLoading,
    tours,
    totalTourCount,
    tourImageMap,
    tourRatingsMap,
    facilityData,
    tryFetchAgain,
    isMapViewOpen,
    toggleMapView,
    tourTimesById,
    selectedTourEvent,
    selectedTourId,
    handleTabChange,
}) {
    const { width } = useWindowSize();
    const isMobile = width < 700;
    const isWidget = SecurityHelper.isWidgetSession();
    const { enableTourTimeFilter } = useFlags();

    const mapComponent = (
        <MapCTAWrapper
            message={`Click below to view the map of ${toTitleCase(facilityData?.facility_name ?? '')}`}
            forceDisplayMap={!isWidget}
        >
            <MapContainer
                isLoading={isMapLoading}
                facilityData={facilityData}
                tours={tours}
                tourImageMap={tourImageMap}
                shouldHideUi={isMobile}
                selectedTourCardId={selectedTourId}
                selectedTourEvent={selectedTourEvent}
            />
        </MapCTAWrapper>
    );

    const hasLoadingError = toursLoadingStatus === STATUS.ERROR;

    return (
        <Stack className="tic-tour-tabs">
            <div className="facility-filter-bar tic-tour-tabs__filter-bar">
                <FacilityCalendarContainer />
                {enableTourTimeFilter && <TourTimeFilter />}
                <AvailabilityAlerts />
            </div>
            <Divider />
            <CardMapLayout mapComponent={mapComponent}>
                {hasLoadingError && (
                    <ErrorAlert
                        loadingStatus={toursLoadingStatus}
                        tryFetchAgain={tryFetchAgain}
                    />
                )}

                {!hasLoadingError && isLoading && <TourListPlaceholder />}

                {totalTourCount !== 0 && !hasLoadingError && !isLoading && (
                    <PaginatedTourList
                        totalTourCount={totalTourCount}
                        tours={tours}
                        tourImageMap={tourImageMap}
                        tourRatingsMap={tourRatingsMap}
                        onCardOverEvent={selectedTourEvent}
                    />
                )}

                {totalTourCount === 0 && !hasLoadingError && !isLoading && (
                    <Box
                        background="gray-1"
                        alignX="center"
                        border="gray"
                        padding="gutter"
                    >
                        <Stack space="sm">
                            <Box alignX="center">
                                <Icon iconName="calendar-busy" size="lg" />
                            </Box>
                            <Text size="lg" align="center" fontWeight="bold">
                                There are currently no tickets for sale at this location
                            </Text>
                            <Text size="md" align="center">
                                Check the&nbsp;
                                <Button
                                    size="sm"
                                    appearance="link"
                                    onClick={() => handleTabChange(1)}
                                >
                                    Seasons & Booking
                                </Button>
                                &nbsp;tab for more information
                            </Text>
                        </Stack>
                    </Box>
                )}
            </CardMapLayout>

            {isMobile && (
                <MapViewWithCards
                    mapComponent={mapComponent}
                    isOpen={isMapViewOpen}
                    onClose={toggleMapView}
                    cards={makeMobileTourListCards({
                        tours,
                        tourImageMap,
                        tourTimesById,
                    })}
                />
            )}
        </Stack>
    );
}

ToursTab.propTypes = {
    isLoading: PropTypes.bool,
    isMapLoading: PropTypes.bool,
    tours: toursType,
    tourImageMap: tourImageMapType,
    tourRatingsMap: tourRatingsMapType,
    facilityData: PropTypes.object,
    totalTourCount: PropTypes.number,
    toursLoadingStatus: PropTypes.string,
    tryFetchAgain: PropTypes.func,
    toggleMapView: PropTypes.func,
    isMapViewOpen: PropTypes.bool,
    tourTimesById: PropTypes.object,
    selectedTourEvent: PropTypes.func,
    selectedTourId: PropTypes.string,
    handleTabChange: PropTypes.func,
};

function ErrorAlert({ loadingStatus, tryFetchAgain }) {
    return (
        <Alert type="error" heading="Network error" shouldFocusOnMount>
            <Stack space="sm">
                <Text>There was a problem getting the tour data.</Text>

                <Box alignX="right">
                    <Button
                        size="xs"
                        appearance="tertiary"
                        iconBeforeElement={<Icon iconName="refresh" />}
                        isLoading={loadingStatus === STATUS.PENDING}
                        onClick={tryFetchAgain}
                    >
                        Try again
                    </Button>
                </Box>
            </Stack>
        </Alert>
    );
}

ErrorAlert.propTypes = {
    loadingStatus: PropTypes.string,
    tryFetchAgain: PropTypes.func,
};
